/**
 * Thin wrapper around amazon-cognito-identity-js that matches the Amplify Auth
 * interface used across this codebase, so callers need no changes beyond swapping
 * the import from "aws-amplify" to "utils/cognito-auth".
 */
import {
  AuthenticationDetails,
  CognitoUser as BaseCognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  UserData,
} from "amazon-cognito-identity-js";

// Augmented user type — adds the fields we set on the user during sign-in
// flows (matches the shape Amplify previously exposed).
export type CognitoUser = BaseCognitoUser & {
  challengeName?: string;
  attributes?: Record<string, string>;
  signInUserSession?: CognitoUserSession;
};

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const config = {
  userPoolId: import.meta.env.VITE_USER_POOL_ID,
  clientId: import.meta.env.VITE_AWS_USER_POOL_WEB_CLIENT_ID,
  authenticationFlowType: "USER_PASSWORD_AUTH" as const,
  cookie: {
    domain: window.location.hostname,
    path: "/",
    expiresDays: 365,
    secure: true,
    sameSite: "Lax" as const,
  },
};

class CookieStorage {
  setItem(key: string, value: string) {
    const expires = new Date();
    expires.setDate(expires.getDate() + config.cookie.expiresDays);
    const secureFlag = config.cookie.secure ? "; Secure" : "";
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; domain=${config.cookie.domain}; path=${config.cookie.path}${secureFlag}; SameSite=${config.cookie.sameSite}`;
  }
  getItem(key: string): string | null {
    const prefix = `${encodeURIComponent(key)}=`;
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : null;
  }
  removeItem(key: string) {
    document.cookie = `${encodeURIComponent(key)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${config.cookie.domain}; path=${config.cookie.path}`;
  }
  clear() {
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=${config.cookie.path}`);
    });
  }
}

const cookieStorage = new CookieStorage();

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.clientId,
  Storage: cookieStorage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeCognitoUser(username: string): CognitoUser {
  return new BaseCognitoUser({ Username: username, Pool: userPool, Storage: cookieStorage });
}

function getCurrentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

function getSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return reject(err ?? new Error("No session"));
      resolve(session);
    });
  });
}

function getUserData(
  user: CognitoUser,
  opts?: { bypassCache?: boolean },
): Promise<UserData> {
  return new Promise((resolve, reject) => {
    user.getUserData(
      (err, data) => {
        if (err || !data) return reject(err ?? new Error("No user data"));
        resolve(data);
      },
      { bypassCache: opts?.bypassCache ?? false },
    );
  });
}

// ---------------------------------------------------------------------------
// Auth API — matches Amplify's Auth.* interface
// ---------------------------------------------------------------------------

export const Auth = {
  // ---- Session & user retrieval ----

  async currentAuthenticatedUser(opts?: { bypassCache?: boolean }): Promise<CognitoUser> {
    const user = getCurrentUser();
    if (!user) throw new Error("No current user");
    // Refresh session — populates signInUserSession on the user
    await getSession(user);
    // Fetch user attributes so callers can read user.attributes.<field>
    const userData = await getUserData(user, { bypassCache: opts?.bypassCache });
    const attributes: Record<string, string> = {};
    for (const attr of userData.UserAttributes ?? []) {
      attributes[attr.Name] = attr.Value;
    }
    user.attributes = attributes;
    return user;
  },

  async currentSession(): Promise<CognitoUserSession> {
    const user = getCurrentUser();
    if (!user) throw new Error("No current user");
    return getSession(user);
  },

  // ---- Sign in ----

  async signIn(
    usernameOrParams: string | { username: string; password: string; validationData?: Record<string, string> },
    password?: string,
    clientMetadata?: Record<string, string>,
  ): Promise<CognitoUser> {
    let username: string;
    let pw: string;
    let validationData: Record<string, string> | undefined;

    if (typeof usernameOrParams === "string") {
      username = usernameOrParams;
      pw = password!;
      validationData = clientMetadata;
    } else {
      username = usernameOrParams.username;
      pw = usernameOrParams.password;
      validationData = usernameOrParams.validationData;
    }

    const user = makeCognitoUser(username);
    user.setAuthenticationFlowType(config.authenticationFlowType);
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: pw,
      ValidationData: validationData,
      ClientMetadata: clientMetadata,
    });

    return new Promise((resolve, reject) => {
      user.authenticateUser(authDetails, {
        onSuccess: () => resolve(user),
        onFailure: reject,
        newPasswordRequired: () => reject(new Error("New password required")),
        mfaRequired: (challengeName) => {
          user.challengeName = challengeName;
          resolve(user);
        },
        totpRequired: (challengeName) => {
          user.challengeName = challengeName;
          resolve(user);
        },
        customChallenge: () => reject(new Error("Custom challenge not supported")),
        mfaSetup: () => {
          user.challengeName = "MFA_SETUP";
          resolve(user);
        },
      });
    });
  },

  // ---- MFA confirm ----

  async confirmSignIn(
    user: CognitoUser | null,
    code: string,
    mfaType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null,
  ): Promise<CognitoUser> {
    if (!user) throw new Error("No user");
    return new Promise((resolve, reject) => {
      const cb = { onSuccess: () => resolve(user), onFailure: reject };
      if (mfaType === "SOFTWARE_TOKEN_MFA") {
        user.sendMFACode(code, cb, "SOFTWARE_TOKEN_MFA");
      } else {
        user.sendMFACode(code, cb);
      }
    });
  },

  // ---- Sign out ----

  async signOut(opts?: { global?: boolean }): Promise<void> {
    const user = getCurrentUser();
    if (!user) return;
    if (opts?.global) {
      return new Promise((resolve, reject) => {
        user.globalSignOut({ onSuccess: () => resolve(), onFailure: reject });
      });
    }
    user.signOut();
  },

  // ---- Sign up ----

  async signUp(params: {
    username: string;
    password: string;
    attributes?: Record<string, string>;
    validationData?: Record<string, string>;
  }): Promise<{ userSub: string }> {
    const attrs = Object.entries(params.attributes ?? {}).map(
      ([Name, Value]) => new CognitoUserAttribute({ Name, Value }),
    );
    const validationData = Object.entries(params.validationData ?? {}).map(
      ([Name, Value]) => new CognitoUserAttribute({ Name, Value }),
    );
    return new Promise((resolve, reject) => {
      userPool.signUp(params.username, params.password, attrs, validationData, (err, result) => {
        if (err) return reject(err);
        resolve({ userSub: result!.userSub });
      });
    });
  },

  async confirmSignUp(username: string, code: string): Promise<void> {
    const user = makeCognitoUser(username);
    return new Promise((resolve, reject) => {
      user.confirmRegistration(code, true, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  async resendSignUp(username: string): Promise<void> {
    const user = makeCognitoUser(username);
    return new Promise((resolve, reject) => {
      user.resendConfirmationCode((err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  // ---- Password ----

  async forgotPassword(username: string): Promise<void> {
    const user = makeCognitoUser(username);
    return new Promise((resolve, reject) => {
      user.forgotPassword({ onSuccess: () => resolve(), onFailure: reject });
    });
  },

  async forgotPasswordSubmit(username: string, code: string, newPassword: string): Promise<void> {
    const user = makeCognitoUser(username);
    return new Promise((resolve, reject) => {
      user.confirmPassword(code, newPassword, { onSuccess: () => resolve(), onFailure: reject });
    });
  },

  async changePassword(user: CognitoUser, oldPassword: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      user.changePassword(oldPassword, newPassword, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  // ---- User attributes ----

  async updateUserAttributes(user: CognitoUser | null, attributes: Record<string, string>): Promise<void> {
    if (!user) throw new Error("No user");
    const attrs = Object.entries(attributes).map(
      ([Name, Value]) => new CognitoUserAttribute({ Name, Value }),
    );
    return new Promise((resolve, reject) => {
      user.updateAttributes(attrs, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  async verifyCurrentUserAttribute(attribute: string): Promise<void> {
    const user = getCurrentUser();
    if (!user) throw new Error("No current user");
    await getSession(user);
    return new Promise((resolve, reject) => {
      user.getAttributeVerificationCode(attribute, {
        onSuccess: () => resolve(),
        onFailure: reject,
      });
    });
  },

  async verifyCurrentUserAttributeSubmit(attribute: string, code: string): Promise<void> {
    const user = getCurrentUser();
    if (!user) throw new Error("No current user");
    await getSession(user);
    return new Promise((resolve, reject) => {
      user.verifyAttribute(attribute, code, {
        onSuccess: () => resolve(),
        onFailure: reject,
      });
    });
  },

  // ---- TOTP / MFA setup ----

  async setupTOTP(user: CognitoUser | null): Promise<string> {
    if (!user) throw new Error("No user");
    return new Promise((resolve, reject) => {
      user.associateSoftwareToken({
        associateSecretCode: resolve,
        onFailure: reject,
      });
    });
  },

  async verifyTotpToken(user: CognitoUser | null, code: string): Promise<void> {
    if (!user) throw new Error("No user");
    return new Promise((resolve, reject) => {
      user.verifySoftwareToken(code, "TOTP device", {
        onSuccess: () => resolve(),
        onFailure: reject,
      });
    });
  },

  async setPreferredMFA(user: CognitoUser | null, mfaType: string): Promise<void> {
    if (!user) throw new Error("No user");
    const wantsSms = mfaType === "SMS_MFA" || mfaType === "SMS";
    const wantsTotp = mfaType === "SOFTWARE_TOKEN_MFA" || mfaType === "TOTP";
    const wantsNone = mfaType === "NOMFA";

    // Read current MFA settings so we don't disable a method the caller isn't
    // touching. Setting a preferred MFA should only change the *preferred*
    // flag — the other method's enabled state must be preserved. Callers who
    // want to remove an MFA method should use disableMFAMethod instead.
    const data = await getUserData(user, { bypassCache: true });
    const enabled = data.UserMFASettingList ?? [];
    const smsCurrentlyEnabled = enabled.includes("SMS_MFA");
    const totpCurrentlyEnabled = enabled.includes("SOFTWARE_TOKEN_MFA");

    const smsMfaSettings = {
      PreferredMfa: wantsSms,
      Enabled: wantsNone ? false : wantsSms || smsCurrentlyEnabled,
    };
    const totpMfaSettings = {
      PreferredMfa: wantsTotp,
      Enabled: wantsNone ? false : wantsTotp || totpCurrentlyEnabled,
    };
    return new Promise((resolve, reject) => {
      user.setUserMfaPreference(smsMfaSettings, totpMfaSettings, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  async disableMFAMethod(
    user: CognitoUser | null,
    method: "SMS_MFA" | "SOFTWARE_TOKEN_MFA",
  ): Promise<void> {
    if (!user) throw new Error("No user");
    // Disable one method while preserving the other. If the disabled method
    // was preferred, the remaining method (if enabled) becomes preferred.
    const data = await getUserData(user, { bypassCache: true });
    const enabled = data.UserMFASettingList ?? [];
    const smsRemains = method !== "SMS_MFA" && enabled.includes("SMS_MFA");
    const totpRemains =
      method !== "SOFTWARE_TOKEN_MFA" && enabled.includes("SOFTWARE_TOKEN_MFA");

    const smsMfaSettings = { PreferredMfa: smsRemains, Enabled: smsRemains };
    const totpMfaSettings = { PreferredMfa: totpRemains, Enabled: totpRemains };
    return new Promise((resolve, reject) => {
      user.setUserMfaPreference(smsMfaSettings, totpMfaSettings, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  },

  async getPreferredMFA(user: CognitoUser | null, opts?: { bypassCache?: boolean }): Promise<string> {
    if (!user) throw new Error("No user");
    // Read PreferredMfaSetting from getUserData; fall back to UserMFASettingList
    // for users with an MFA method enabled but no explicit preference. Returns
    // Cognito challenge names ("SMS_MFA" / "SOFTWARE_TOKEN_MFA" / "NOMFA") to
    // match Amplify's shape.
    const data = await getUserData(user, { bypassCache: opts?.bypassCache });
    const preferred = data.PreferredMfaSetting;
    if (preferred === "SMS_MFA" || preferred === "SOFTWARE_TOKEN_MFA") {
      return preferred;
    }
    const enabled = data.UserMFASettingList ?? [];
    if (enabled.includes("SOFTWARE_TOKEN_MFA")) return "SOFTWARE_TOKEN_MFA";
    if (enabled.includes("SMS_MFA")) return "SMS_MFA";
    return "NOMFA";
  },
};
